import { z } from 'zod';
import type { CreateQuoteCommand, PhaseId, QuoteDto, UpdateQuoteCommand } from '@/api/generated';
import { useCreateQuote, useUpdateQuote } from '@/api/hooks';
import { Btn } from '@/design-system/primitives/Btn/Btn';
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/design-system/primitives/Dialog/Dialog';
import { Form, FormField, FormSubmitButton } from '@/design-system/primitives/Form/Form';
import { Input } from '@/design-system/primitives/Input/Input';
import styles from './QuoteDialogs.module.css';

const PHASE_OPTIONS: Array<{ id: PhaseId; label: string }> = [
  { id: 'Voor', label: 'Voorbereiding' },
  { id: 'Ontw', label: 'Ontwerp' },
  { id: 'Verg', label: 'Vergunningen' },
  { id: 'Aanb', label: 'Aanbesteding' },
  { id: 'Ruw', label: 'Ruwbouw' },
  { id: 'Wwd', label: 'Wind-/waterdicht' },
  { id: 'Tech', label: 'Technieken' },
  { id: 'Afw', label: 'Afwerking' },
  { id: 'Opl', label: 'Oplevering' },
  { id: 'Naz', label: 'Nazorg' },
];

const schema = z.object({
  supplier: z.string().trim().min(2, 'Minstens 2 tekens'),
  subject: z.string().trim().min(2, 'Minstens 2 tekens'),
  city: z.string().trim().min(2, 'Minstens 2 tekens'),
  version: z.string().trim().min(1, 'Verplicht veld'),
  issuedAt: z.string().min(1, 'Kies een datum'),
  total: z
    .string()
    .min(1, 'Verplicht veld')
    .refine((v) => /^\d+([.,]\d{1,2})?$/.test(v.trim()), 'Bedrag in euro (bv. 18500)'),
  score: z
    .string()
    .min(1, 'Verplicht veld')
    .refine((v) => {
      const n = Number(v.replace(',', '.'));
      return Number.isFinite(n) && n >= 0 && n <= 10;
    }, 'Score 0–10'),
  phaseId: z.string().min(1, 'Kies een fase'),
});

type QuoteFormValues = z.infer<typeof schema>;

function FieldsBody() {
  return (
    <>
      <FormField<QuoteFormValues> name="supplier" label="Leverancier" required>
        {(field) => <Input {...field} autoFocus placeholder="Bv. Pollet Ramen" />}
      </FormField>
      <FormField<QuoteFormValues> name="subject" label="Onderwerp" required>
        {(field) => <Input {...field} placeholder="Bv. Buitenschrijnwerk" />}
      </FormField>
      <FormField<QuoteFormValues> name="city" label="Stad" required>
        {(field) => <Input {...field} placeholder="Bv. Leuven" />}
      </FormField>
      <FormField<QuoteFormValues> name="version" label="Versie" required>
        {(field) => <Input {...field} placeholder="v1" />}
      </FormField>
      <FormField<QuoteFormValues> name="issuedAt" label="Datum offerte" required>
        {(field) => (
          <Input
            {...field}
            value={(field.value as string) ?? ''}
            type="date"
            onChange={(e) => field.onChange(e.target.value)}
          />
        )}
      </FormField>
      <FormField<QuoteFormValues> name="total" label="Totaal (€)" required>
        {(field) => (
          <Input
            {...field}
            value={(field.value as string) ?? ''}
            inputMode="decimal"
            placeholder="18500"
          />
        )}
      </FormField>
      <FormField<QuoteFormValues> name="score" label="Score (0–10)" required>
        {(field) => (
          <Input
            {...field}
            value={(field.value as string) ?? ''}
            inputMode="decimal"
            placeholder="8"
          />
        )}
      </FormField>
      <FormField<QuoteFormValues> name="phaseId" label="Fase" required>
        {(field) => (
          <select
            {...field}
            className={styles.select}
            value={(field.value as string) ?? ''}
            onChange={(e) => field.onChange(e.target.value)}
          >
            {PHASE_OPTIONS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        )}
      </FormField>
    </>
  );
}

export function QuoteCreateDialog({
  open,
  onOpenChange,
  defaultPhaseId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultPhaseId?: PhaseId;
}) {
  const createMutation = useCreateQuote();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle>Nieuwe offerte</DialogTitle>
        <DialogDescription>Voeg een offerte toe aan de vergelijker.</DialogDescription>
        <DialogBody>
          <Form<QuoteFormValues>
            schema={schema}
            defaultValues={{
              supplier: '',
              subject: '',
              city: '',
              version: 'v1',
              issuedAt: '',
              total: '',
              score: '7',
              phaseId: defaultPhaseId ?? 'Wwd',
            }}
            onSubmit={async (values) => {
              const body: CreateQuoteCommand = {
                supplier: values.supplier.trim(),
                subject: values.subject.trim(),
                city: values.city.trim(),
                version: values.version.trim(),
                issuedAt: values.issuedAt,
                total: values.total.replace(',', '.'),
                score: values.score.replace(',', '.'),
                phaseId: values.phaseId,
              };
              await createMutation.mutateAsync(body);
              onOpenChange(false);
            }}
          >
            <FieldsBody />
            <DialogFooter>
              <DialogClose asChild>
                <Btn type="button">Annuleer</Btn>
              </DialogClose>
              <FormSubmitButton>Toevoegen</FormSubmitButton>
            </DialogFooter>
          </Form>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

export function QuoteEditDialog({
  open,
  onOpenChange,
  quote,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  quote: QuoteDto;
}) {
  const updateMutation = useUpdateQuote(quote.id);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle>Offerte bewerken</DialogTitle>
        <DialogDescription>Pas leverancier, prijs, score of fase aan.</DialogDescription>
        <DialogBody>
          <Form<QuoteFormValues>
            schema={schema}
            defaultValues={{
              supplier: quote.supplier,
              subject: quote.subject,
              city: quote.city,
              version: quote.version,
              issuedAt: (quote.issuedAt ?? '').slice(0, 10),
              total: String(quote.total ?? ''),
              score: String(quote.score ?? ''),
              phaseId: quote.phaseId,
            }}
            onSubmit={async (values) => {
              const body: UpdateQuoteCommand = {
                supplier: values.supplier.trim() || null,
                subject: values.subject.trim() || null,
                city: values.city.trim() || null,
                version: values.version.trim() || null,
                issuedAt: values.issuedAt || null,
                total: values.total.replace(',', '.'),
                score: values.score.replace(',', '.'),
                phaseId: values.phaseId,
              };
              await updateMutation.mutateAsync(body);
              onOpenChange(false);
            }}
          >
            <FieldsBody />
            <DialogFooter>
              <DialogClose asChild>
                <Btn type="button">Annuleer</Btn>
              </DialogClose>
              <FormSubmitButton>Opslaan</FormSubmitButton>
            </DialogFooter>
          </Form>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
